'use client';

import { useEffect, useState } from 'react';
import { useFirestore } from '@/firebase/index';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  Facebook,
  Youtube,
  Instagram,
  Twitter,
  Mail,
} from 'lucide-react';
import Link from 'next/link';
import type { MembershipInfoField, SocialMediaPlatform } from '@/models/membership-info';

// Discord Icon Component
const DiscordIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 127.14 96.36"
    className={className}
    fill="currentColor"
  >
    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
  </svg>
);

// Twitch Icon Component
const TwitchIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
  >
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
  </svg>
);

// Helper function to get the right icon for a social media platform
const getSocialIcon = (platform: SocialMediaPlatform) => {
  switch (platform) {
    case 'discord':
      return DiscordIcon;
    case 'facebook':
      return Facebook;
    case 'youtube':
      return Youtube;
    case 'instagram':
      return Instagram;
    case 'twitter':
      return Twitter;
    case 'twitch':
      return TwitchIcon;
    default:
      return null;
  }
};

// Helper function to get platform name
const getPlatformName = (platform: SocialMediaPlatform): string => {
  const names: Record<SocialMediaPlatform, string> = {
    discord: 'Discord',
    facebook: 'Facebook',
    youtube: 'YouTube',
    instagram: 'Instagram',
    twitter: 'Twitter',
    twitch: 'Twitch',
  };
  return names[platform];
};

// Helper function to get platform color
const getPlatformColor = (platform: SocialMediaPlatform): string => {
  const colors: Record<SocialMediaPlatform, string> = {
    discord: 'hover:text-[#5865F2]', // Discord brand color
    twitch: 'hover:text-[#9146FF]', // Twitch brand color
    facebook: 'hover:text-primary',
    youtube: 'hover:text-primary',
    instagram: 'hover:text-primary',
    twitter: 'hover:text-primary',
  };
  return colors[platform];
};

interface SocialMediaLinksProps {
  className?: string;
  iconClassName?: string;
  variant?: 'default' | 'compact';
}

export function SocialMediaLinks({ className, iconClassName = 'h-5 w-5', variant = 'default' }: SocialMediaLinksProps) {
  const firestore = useFirestore();
  const [socialLinks, setSocialLinks] = useState<MembershipInfoField[]>([]);
  const [loading, setLoading] = useState(true);

  // Load social media links from Firestore
  useEffect(() => {
    const loadSocialLinks = async () => {
      try {
        const membershipInfoRef = collection(firestore, 'membershipInfo');
        const q = query(membershipInfoRef, where('type', '==', 'socialMedia'));
        const snapshot = await getDocs(q);
        
        const links: MembershipInfoField[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as MembershipInfoField;
          // Only include links that have a value (URL)
          if (data.value && data.value.trim() && data.platform) {
            links.push({ ...data, id: doc.id });
          }
        });
        
        // Sort by order
        links.sort((a, b) => (a.order || 0) - (b.order || 0));
        setSocialLinks(links);
      } catch (error) {
        console.error('Error loading social media links:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSocialLinks();
  }, [firestore]);

  if (loading || socialLinks.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {/* Contact Email Link */}
      <Link
        href="/contact"
        className="text-primary hover:text-primary/80 transition-colors"
        aria-label="Contact Us"
        title="Contact Us"
      >
        <Mail className={iconClassName} />
      </Link>
      
      {/* Social Media Links */}
      {socialLinks.map((link) => {
        const Icon = getSocialIcon(link.platform as SocialMediaPlatform);
        if (!Icon) return null;
        
        const platformName = getPlatformName(link.platform as SocialMediaPlatform);
        const platformColor = getPlatformColor(link.platform as SocialMediaPlatform);
        
        return (
          <a
            key={link.id}
            href={link.value}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-muted-foreground ${platformColor} transition-colors`}
            aria-label={`Join us on ${platformName}`}
            title={platformName}
          >
            <Icon className={iconClassName} />
          </a>
        );
      })}
    </div>
  );
}
